import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { PrimaryBtn } from 'components/UI/Buttons';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { GmailEmail, GmailMessage, MeetingOverview } from 'services/api/googleWorkspace.api';
import { useLazyGetGmailOverviewQuery, useLazyGetMeetOverviewQuery } from 'services/api/googleWorkspace.api';
import {
  useDisconnectGmailMutation,
  useDisconnectMeetMutation,
  useGetGoogleWorkspaceStatusQuery,
  useLazyGetGmailConnectUrlQuery,
  useLazyGetMeetConnectUrlQuery,
} from 'services/api/user.api';

const NAVY = 'theme.palette.primaryDark.main';
const SURFACE_LOW = '#F0F4F8';
const SURFACE_CARD = '#ffffff';

function formatDate(isoString?: string): string {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('es-CL', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

const MeetingCard = ({ meeting }: { meeting: MeetingOverview }) => {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        bgcolor: SURFACE_CARD,
        borderRadius: '16px !important',
        '&:before': { display: 'none' },
        boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.08)',
        overflow: 'hidden',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: NAVY, opacity: 0.5 }} />} sx={{ px: 3, py: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 700, fontFamily: 'Jost, sans-serif', color: NAVY, lineHeight: 1.3 }}
          >
            {meeting.title ?? meeting.meetingUri ?? meeting.name}
          </Typography>
          <Typography variant="caption" sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
            {formatDate(meeting.startTime)} – {formatDate(meeting.endTime)}
          </Typography>
          {meeting.participants.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {meeting.participants.map((p, i) => (
                <Chip
                  key={p.email ?? p.displayName ?? i}
                  icon={<PersonIcon sx={{ fontSize: '13px !important', opacity: 0.7 }} />}
                  label={p.email ?? p.displayName ?? 'Participante'}
                  size="small"
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.72rem',
                    bgcolor: SURFACE_LOW,
                    color: NAVY,
                    fontWeight: 500,
                    '& .MuiChip-icon': { color: NAVY },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}>
        <Box sx={{ height: '1px', bgcolor: `rgba(21, 24, 58, 0.06)`, mb: 2 }} />
        {!meeting.docText ? (
          <Typography variant="body2" sx={{ color: NAVY, opacity: 0.45, fontFamily: 'Jost, sans-serif' }}>
            Esta reunión no tiene notas disponibles.
          </Typography>
        ) : (
          <Box
            component="pre"
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: NAVY,
              bgcolor: SURFACE_LOW,
              borderRadius: 2,
              p: 2,
              m: 0,
              maxHeight: 400,
              overflowY: 'auto',
              lineHeight: 1.6,
            }}
          >
            {meeting.docText}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const MeetSection = ({ connected }: { connected: boolean }) => {
  const [fetchOverview, { data: meetings, isFetching, isError, isUninitialized }] = useLazyGetMeetOverviewQuery();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: SURFACE_LOW,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <VideoCallIcon sx={{ color: NAVY, fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, lineHeight: 1.2 }}
            >
              Reuniones recientes
            </Typography>
            {connected && (
              <Typography variant="caption" sx={{ color: NAVY, opacity: 0.45, fontFamily: 'Jost, sans-serif' }}>
                Google Meet
              </Typography>
            )}
          </Box>
        </Box>
        {connected && (
          <PrimaryBtn
            variant="contained"
            size="small"
            onClick={() => void fetchOverview(undefined)}
            disabled={isFetching}
            sx={{ borderRadius: '1.5rem', minWidth: 120 }}
          >
            {isFetching ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : 'Cargar'}
          </PrimaryBtn>
        )}
      </Box>

      {!connected && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Conecta Google Meet para ver tus reuniones recientes aquí.
        </Alert>
      )}
      {isError && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          No se pudieron cargar las reuniones. Verifica que tu token sigue vigente.
        </Alert>
      )}
      {!isUninitialized && !isFetching && !isError && meetings?.length === 0 && (
        <Typography variant="body2" sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
          No se encontraron reuniones recientes.
        </Typography>
      )}

      {meetings && meetings.length > 0 && (
        <Stack spacing={2}>
          {meetings.map((meeting) => (
            <MeetingCard key={meeting.name} meeting={meeting} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const GmailMessageBubble = ({ msg, index }: { msg: GmailMessage; index: number }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    {index > 0 && <Box sx={{ height: '1px', bgcolor: 'rgba(21, 24, 58, 0.06)', my: 1.5 }} />}
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
      <Typography variant="caption" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY, opacity: 0.7 }}>
        {msg.from ?? '—'}
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.35 }}>
        {msg.date ? formatDate(msg.date) : '—'}
      </Typography>
    </Box>
    <Typography
      variant="body2"
      sx={{
        fontFamily: 'Jost, sans-serif',
        color: NAVY,
        opacity: 0.75,
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {msg.body?.trim() ?? '(sin contenido)'}
    </Typography>
  </Box>
);

const GmailEmailCard = ({ email }: { email: GmailEmail }) => (
  <Accordion
    disableGutters
    elevation={0}
    sx={{
      bgcolor: SURFACE_CARD,
      borderRadius: '16px !important',
      '&:before': { display: 'none' },
      boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.08)',
      overflow: 'hidden',
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: NAVY, opacity: 0.5 }} />} sx={{ px: 3, py: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
        <Typography
          variant="body1"
          sx={{ fontWeight: 700, fontFamily: 'Jost, sans-serif', color: NAVY, lineHeight: 1.3 }}
        >
          {email.subject ?? '(sin asunto)'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
            {email.from ?? '—'}
          </Typography>
          {email.messages.length > 1 && (
            <Chip
              label={email.messages.length}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontFamily: 'Jost, sans-serif',
                bgcolor: SURFACE_LOW,
                color: NAVY,
              }}
            />
          )}
        </Box>
        <Typography variant="caption" sx={{ color: NAVY, opacity: 0.35, fontFamily: 'Jost, sans-serif' }}>
          {email.date ? formatDate(email.date) : '—'}
        </Typography>
      </Box>
    </AccordionSummary>

    <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}>
      <Box sx={{ height: '1px', bgcolor: 'rgba(21, 24, 58, 0.06)', mb: 2 }} />
      {email.messages.length === 0 ? (
        <Typography variant="body2" sx={{ color: NAVY, opacity: 0.45, fontFamily: 'Jost, sans-serif' }}>
          Sin contenido disponible.
        </Typography>
      ) : (
        <Stack spacing={0}>
          {email.messages.map((msg, i) => (
            <GmailMessageBubble key={msg.id ?? i} msg={msg} index={i} />
          ))}
        </Stack>
      )}
    </AccordionDetails>
  </Accordion>
);

const GmailSection = ({ connected }: { connected: boolean }) => {
  const [fetchOverview, { data: emails, isFetching, isError, isUninitialized }] = useLazyGetGmailOverviewQuery();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: SURFACE_LOW,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EmailIcon sx={{ color: NAVY, fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, lineHeight: 1.2 }}
            >
              Correos recientes
            </Typography>
            {connected && (
              <Typography variant="caption" sx={{ color: NAVY, opacity: 0.45, fontFamily: 'Jost, sans-serif' }}>
                Gmail · Bandeja de entrada
              </Typography>
            )}
          </Box>
        </Box>
        {connected && (
          <PrimaryBtn
            variant="contained"
            size="small"
            onClick={() => void fetchOverview(undefined)}
            disabled={isFetching}
            sx={{ borderRadius: '1.5rem', minWidth: 120 }}
          >
            {isFetching ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : 'Cargar'}
          </PrimaryBtn>
        )}
      </Box>

      {!connected && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Conecta Gmail para ver los correos recientes aquí.
        </Alert>
      )}
      {isError && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          No se pudieron cargar los correos. Verifica que tu token sigue vigente.
        </Alert>
      )}
      {!isUninitialized && !isFetching && !isError && (!emails || emails.length === 0) && (
        <Typography variant="body2" sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
          No se encontraron correos en la bandeja de entrada.
        </Typography>
      )}
      {emails && emails.length > 0 && (
        <Stack spacing={2}>
          {emails.map((email) => (
            <GmailEmailCard key={email.id} email={email} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const DisconnectDialog = ({
  open,
  label,
  onConfirm,
  onCancel,
  isLoading,
}: {
  open: boolean;
  label: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) => (
  <Dialog
    open={open}
    onClose={onCancel}
    slotProps={{
      paper: {
        sx: {
          borderRadius: 4,
          backdropFilter: 'blur(16px)',
          background: 'rgba(255,255,255,0.92)',
          boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.14)',
          p: 1,
        },
      },
    }}
  >
    <DialogTitle sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY }}>Desconectar {label}</DialogTitle>
    <DialogContent>
      <DialogContentText sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.7 }}>
        Se eliminará el acceso de {label} y se cancelarán las notificaciones push. ¿Deseas continuar?
      </DialogContentText>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
      <Button
        onClick={onCancel}
        disabled={isLoading}
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontWeight: 600,
          color: NAVY,
          borderRadius: '1.5rem',
          textTransform: 'none',
        }}
      >
        Cancelar
      </Button>
      <Button
        onClick={onConfirm}
        disabled={isLoading}
        variant="contained"
        color="error"
        sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, borderRadius: '1.5rem', textTransform: 'none' }}
      >
        {isLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Desconectar'}
      </Button>
    </DialogActions>
  </Dialog>
);

const IntegrationRow = ({
  icon,
  label,
  description,
  connected,
  onConnect,
  onDisconnect,
  isLoading,
  isDisconnecting,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  connected: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  isLoading: boolean;
  isDisconnecting: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 2.5,
      borderRadius: 3,
      bgcolor: SURFACE_CARD,
      boxShadow: '0px 4px 16px rgba(21, 24, 58, 0.05)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '12px',
          bgcolor: SURFACE_LOW,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: NAVY,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
          {description}
        </Typography>
      </Box>
    </Box>

    {connected ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          icon={<CheckCircleIcon />}
          label="Conectado"
          color="success"
          size="small"
          sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600 }}
        />
        <IconButton
          onClick={onDisconnect}
          disabled={isDisconnecting}
          size="small"
          title={`Desconectar ${label}`}
          sx={{
            color: NAVY,
            opacity: 0.4,
            '&:hover': { opacity: 0.8, color: 'error.main' },
          }}
        >
          {isDisconnecting ? <CircularProgress size={16} /> : <LinkOffIcon fontSize="small" />}
        </IconButton>
      </Box>
    ) : (
      <PrimaryBtn
        variant="contained"
        onClick={onConnect}
        disabled={isLoading}
        size="small"
        sx={{ borderRadius: '1.5rem', minWidth: 130 }}
      >
        {isLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : `Conectar ${label}`}
      </PrimaryBtn>
    )}
  </Box>
);

const GoogleIntegrations = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [successIntegration, setSuccessIntegration] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState<'gmail' | 'meet' | null>(null);

  const { data: status, refetch } = useGetGoogleWorkspaceStatusQuery(undefined);
  const [triggerGmailUrl, { isFetching: isLoadingGmail }] = useLazyGetGmailConnectUrlQuery();
  const [triggerMeetUrl, { isFetching: isLoadingMeet }] = useLazyGetMeetConnectUrlQuery();
  const [disconnectGmail, { isLoading: isDisconnectingGmail }] = useDisconnectGmailMutation();
  const [disconnectMeet, { isLoading: isDisconnectingMeet }] = useDisconnectMeetMutation();

  useEffect(() => {
    const success = searchParams.get('google_success');
    const error = searchParams.get('google_error');
    if (success) {
      setSuccessIntegration(success);
      void refetch();
      searchParams.delete('google_success');
      setSearchParams(searchParams, { replace: true });
    }
    if (error) {
      setErrorCode(error);
      searchParams.delete('google_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [refetch, searchParams, setSearchParams]);

  const handleConnectGmail = async () => {
    const url = await triggerGmailUrl(undefined).unwrap();
    window.location.href = url;
  };

  const handleConnectMeet = async () => {
    const url = await triggerMeetUrl(undefined).unwrap();
    window.location.href = url;
  };

  const handleConfirmDisconnect = async () => {
    if (!confirmDisconnect) return;
    try {
      if (confirmDisconnect === 'gmail') await disconnectGmail(undefined).unwrap();
      else await disconnectMeet(undefined).unwrap();
      void refetch();
    } finally {
      setConfirmDisconnect(null);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 5, gap: 1.5 }}>
        <IconButton
          onClick={() => navigate('/profile')}
          sx={{
            color: NAVY,
            bgcolor: SURFACE_CARD,
            boxShadow: '0px 4px 12px rgba(21, 24, 58, 0.08)',
            '&:hover': { bgcolor: SURFACE_CARD, opacity: 0.85 },
            width: 40,
            height: 40,
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '14px',
            bgcolor: NAVY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SmartToyIcon sx={{ fontSize: 24, color: '#F9D00A' }} />
        </Box>
        <Box>
          <Typography
            variant="h5"
            sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, lineHeight: 1.1 }}
          >
            Integraciones Google
          </Typography>
          <Typography variant="body2" sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
            Conecta tus herramientas para potenciar el agente IA
          </Typography>
        </Box>
      </Box>

      {successIntegration && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          ¡{successIntegration === 'gmail' ? 'Gmail' : 'Google Meet'} conectado exitosamente!
        </Alert>
      )}
      {errorCode && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          Error al conectar con Google: {errorCode}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Connections */}
        <Box
          sx={{
            p: 3,
            borderRadius: 4,
            bgcolor: SURFACE_CARD,
            boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.06)',
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 700,
              color: NAVY,
              opacity: 0.4,
              letterSpacing: '0.1em',
              display: 'block',
              mb: 2,
            }}
          >
            Conexiones
          </Typography>
          <Stack spacing={1.5}>
            <IntegrationRow
              icon={<EmailIcon sx={{ fontSize: 20 }} />}
              label="Gmail"
              description="Leer y recibir correos de tus clientes"
              connected={status?.gmailConnected ?? false}
              onConnect={() => void handleConnectGmail()}
              onDisconnect={() => setConfirmDisconnect('gmail')}
              isLoading={isLoadingGmail}
              isDisconnecting={isDisconnectingGmail}
            />
            <IntegrationRow
              icon={<VideoCallIcon sx={{ fontSize: 20 }} />}
              label="Google Meet"
              description="Acceder a transcripts de videollamadas"
              connected={status?.meetConnected ?? false}
              onConnect={() => void handleConnectMeet()}
              onDisconnect={() => setConfirmDisconnect('meet')}
              isLoading={isLoadingMeet}
              isDisconnecting={isDisconnectingMeet}
            />
          </Stack>
        </Box>

        {/* Gmail threads */}
        <Box
          sx={{
            p: 3,
            borderRadius: 4,
            bgcolor: SURFACE_CARD,
            boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.06)',
          }}
        >
          <GmailSection connected={status?.gmailConnected ?? false} />
        </Box>

        {/* Meet overview */}
        <Box
          sx={{
            p: 3,
            borderRadius: 4,
            bgcolor: SURFACE_CARD,
            boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.06)',
          }}
        >
          <MeetSection connected={status?.meetConnected ?? false} />
        </Box>
      </Stack>

      <DisconnectDialog
        open={confirmDisconnect !== null}
        label={confirmDisconnect === 'gmail' ? 'Gmail' : 'Google Meet'}
        onConfirm={() => void handleConfirmDisconnect()}
        onCancel={() => setConfirmDisconnect(null)}
        isLoading={isDisconnectingGmail || isDisconnectingMeet}
      />
    </Container>
  );
};

export default GoogleIntegrations;
